/* # EasySite Notification Helper Utilities
   - Manages creation of in-app and email notifications using EasySite APIs
   - Table ID 70655: Notifications
   - Table ID 70673: User Preferences
   - Table ID 70648: Users Profile (Primary source for user data and emails)
*/

const TABLES = {
  NOTIFICATIONS: 70655,
  PREFERENCES: 70673,
  USERS_PROFILE: 70648
};

export const NOTIFICATION_TYPES = {
  MENTION: 'mention',
  FAULT_NEW: 'fault_new',
  FAULT_STATUS: 'fault_status',
  FAULT_COMMENT: 'fault_comment',
  TOPIC_NEW: 'topic_new',
  TOPIC_REPLY: 'topic_reply',
  ANNOUNCEMENT: 'announcement',
  PASSWORD_RESET: 'password_reset',
  INVITATION: 'invitation'
};

/**
 * Helper to get the public URL for emails
 * Always returns the custom domain https://wj.gr as requested
 */
const getEmailBaseUrl = () => {
  return 'https://wj.gr';
};

/**
 * Helper to get translated string using window.i18next
 */
function getTranslation(key, lang, variables = {}) {
  if (window.i18next) {
    return window.i18next.t(key, { lng: lang, ...variables });
  }
  // Fallback if i18next is not ready
  return key;
}

/**
 * Fetches user notification preferences from EasySite Table 70673
 */
export async function getUserPreferences(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await window.ezsite.apis.tablePage(TABLES.PREFERENCES, {
      PageNo: 1,
      PageSize: 1,
      Filters: [{ name: 'user_id', op: 'Equal', value: Number(userId) }]
    });

    if (error || !data?.List?.[0]) {
      return {
        user_id: Number(userId),
        notify_on_mentions: true,
        notify_property_topics: true,
        notify_building_topics: true,
        notify_topic_replies: true,
        notify_property_faults: true,
        notify_building_faults: true,
        notify_fault_status: true,
        notify_announcements: true,
        email_notifications: true,
        language: 'en'
      };
    }
    return {
      ...data.List[0],
      language: data.List[0].language || 'en'
    };
  } catch (e) {
    console.error('[NotificationSystem] Failed to get preferences for user', userId, e);
    return null;
  }
}

/**
 * Get user email from the users profile table (70648)
 */
async function getUserEmail(userId) {
  try {
    const { data, error } = await window.ezsite.apis.tablePage(TABLES.USERS_PROFILE, {
      PageNo: 1,
      PageSize: 1,
      Filters: [{ name: 'id', op: 'Equal', value: Number(userId) }]
    });

    if (error || !data?.List?.[0]) return null;

    const user = data.List[0];
    return user.email || user.Email || null;
  } catch (e) {
    console.error(`[NotificationSystem] Error fetching email for user ${userId}:`, e);
    return null;
  }
}

/**
 * Core function to write a notification to EasySite Table 70655
 */
async function notify(userId, type, title, message, link) {
  if (!userId || isNaN(Number(userId))) return;

  try {
    const payload = {
      user_id: Number(userId),
      type: String(type),
      title: String(title),
      message: String(message),
      link: link || '',
      is_read: false,
      created_at: new Date().toISOString()
    };

    const { data, error } = await window.ezsite.apis.tableCreate(TABLES.NOTIFICATIONS, payload);
    if (error) return null;

    triggerEmailIfEnabled(userId, title, message, link).catch((e) => console.error('[NotificationSystem] Email failed:', e));
    return data;
  } catch (e) {
    console.error('[NotificationSystem] Internal error during notify():', e);
    return null;
  }
}

/**
 * Checks email preferences and sends via EasySite sendEmail API
 */
async function triggerEmailIfEnabled(userId, title, message, link) {
  try {
    const prefs = await getUserPreferences(userId);
    if (prefs && prefs.email_notifications === false) return;

    const email = await getUserEmail(userId);
    if (!email) return;

    const userLang = prefs?.language || 'en';
    const baseUrl = getEmailBaseUrl();
    const fullLink = link ? `${baseUrl}${link}` : baseUrl;

    const viewDetailsText = getTranslation('email.viewDetails', userLang);
    const footerText = getTranslation('email.footer', userLang);

    await window.ezsite.apis.sendEmail({
      from: 'ManageHub <noreply@managehub.com>',
      to: [email],
      subject: `[ManageHub] ${title}`,
      text: `${title}\n\n${message}\n\nView details: ${fullLink}`,
      html: `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 30px; border-radius: 16px 16px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 700;">ManageHub</h1>
          </div>
          <div style="background: white; padding: 40px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 16px 16px;">
            <h2 style="color: #1f2937; font-size: 20px; font-weight: 600; margin: 0 0 20px 0;">${title}</h2>
            <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">${message}</p>
            <div style="text-align: center; margin: 30px 0;">
              <a href="${fullLink}" style="display: inline-block; background: #3b82f6; color: white; padding: 14px 32px; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 16px;">${viewDetailsText}</a>
            </div>
            <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">${footerText}</p>
            </div>
          </div>
        </div>
      `
    });
  } catch (e) {
    console.error('[NotificationSystem] triggerEmailIfEnabled failed:', e);
  }
}

/**
 * Sends a dedicated invitation email to new users
 */
export async function sendInvitationEmail(userEmail, userName, password, userLanguage = 'en') {
  console.log(`[NotificationSystem] Attempting to send invitation to ${userEmail} in ${userLanguage}`);
  try {
    const baseUrl = getEmailBaseUrl();
    const lang = userLanguage || 'en';

    const subject = getTranslation('email.invitation.subject', lang);
    const greeting = getTranslation('email.invitation.greeting', lang, { name: userName || 'User' });
    const body = getTranslation('email.invitation.body', lang);
    const credentialsTitle = getTranslation('email.invitation.credentialsTitle', lang);
    const emailLabel = getTranslation('email.invitation.emailLabel', lang);
    const passwordLabel = getTranslation('email.invitation.passwordLabel', lang);
    const urgencyNote = getTranslation('email.invitation.urgencyNote', lang);
    const actionButton = getTranslation('email.invitation.actionButton', lang);
    const securityTip = getTranslation('email.invitation.securityTip', lang);
    const automatedText = getTranslation('email.automatedMessage', lang);

    const emailPayload = {
      from: 'ManageHub <noreply@managehub.com>',
      to: [userEmail],
      subject: `[ManageHub] ${subject}`,
      text: `${subject}\n\n${greeting}\n\n${body}\n\n${credentialsTitle}\n${emailLabel} ${userEmail}\n${passwordLabel} ${password}\n\n${urgencyNote}\n\nSign in at: ${baseUrl}`,
      html: `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 30px; border-radius: 16px 16px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: 800; letter-spacing: -0.02em;">ManageHub</h1>
          </div>
          <div style="background: white; padding: 40px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 16px 16px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);">
            <h2 style="color: #111827; font-size: 22px; font-weight: 700; margin: 0 0 24px 0;">${subject}</h2>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 16px 0;">${greeting}</p>
            <p style="color: #374151; font-size: 16px; line-height: 1.6; margin: 0 0 32px 0;">${body}</p>
            
            <div style="background: #f8fafc; border-radius: 12px; padding: 24px; margin-bottom: 32px; border: 1px solid #e2e8f0; border-left: 4px solid #3b82f6;">
              <p style="color: #1e293b; font-size: 14px; margin: 0 0 16px 0; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em;">${credentialsTitle}</p>
              <div style="margin-bottom: 12px;">
                <span style="color: #64748b; font-size: 13px; display: block; margin-bottom: 2px;">${emailLabel}</span>
                <span style="color: #0f172a; font-size: 16px; font-weight: 600;">${userEmail}</span>
              </div>
              <div>
                <span style="color: #64748b; font-size: 13px; display: block; margin-bottom: 2px;">${passwordLabel}</span>
                <code style="background: #ffffff; color: #2563eb; padding: 6px 12px; border: 1px solid #cbd5e1; border-radius: 6px; font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace; font-size: 18px; font-weight: 700; display: inline-block;">${password}</code>
              </div>
            </div>

            <div style="background: #fff1f2; border-radius: 8px; padding: 16px; margin-bottom: 32px; border: 1px solid #fecdd3;">
              <p style="color: #be123c; font-size: 15px; line-height: 1.5; margin: 0; font-weight: 600;">⚠️ ${urgencyNote}</p>
            </div>

            <div style="text-align: center; margin-bottom: 32px;">
              <a href="${baseUrl}" style="display: inline-block; background: #2563eb; color: white; padding: 16px 40px; text-decoration: none; border-radius: 12px; font-weight: 700; font-size: 16px; box-shadow: 0 4px 6px -1px rgba(37, 99, 235, 0.2); transition: all 0.2s ease;">${actionButton}</a>
              <p style="margin-top: 12px; color: #64748b; font-size: 13px;">${baseUrl}</p>
            </div>

            <p style="color: #94a3b8; font-size: 13px; line-height: 1.5; margin: 0;">${securityTip}</p>
            
            <div style="margin-top: 48px; padding-top: 24px; border-top: 1px solid #f1f5f9; text-align: center;">
              <p style="color: #94a3b8; font-size: 12px; margin: 0;">${automatedText}</p>
            </div>
          </div>
        </div>
      `
    };

    const result = await window.ezsite.apis.sendEmail(emailPayload);
    console.log('[NotificationSystem] sendEmail result:', result);

    return !result.error;
  } catch (e) {
    console.error('[NotificationSystem] Invitation email trigger failed:', e);
    return false;
  }
}

/**
 * Sends a dedicated password reset email
 */
export async function sendPasswordResetEmail(email, userName, userLanguage = 'en') {
  try {
    const baseUrl = getEmailBaseUrl();
    const lang = userLanguage || 'en';

    const subject = getTranslation('email.passwordReset.title', lang);
    const greeting = getTranslation('email.passwordReset.greeting', lang, { name: userName || 'User' });
    const body = getTranslation('email.passwordReset.body', lang);
    const securityNote = getTranslation('email.passwordReset.securityNote', lang);
    const securityBody = getTranslation('email.passwordReset.securityBody', lang);
    const instruction = getTranslation('email.passwordReset.instruction', lang);
    const signInText = getTranslation('email.signIn', lang);
    const automatedText = getTranslation('email.automatedMessage', lang);

    const result = await window.ezsite.apis.sendEmail({
      from: 'ManageHub <noreply@managehub.com>',
      to: [email],
      subject: `[ManageHub] ${subject}`,
      text: `${subject}\n\n${greeting}\n\n${body}\n\n${securityNote}\n${securityBody}`,
      html: `
        <div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px;">
          <div style="background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); padding: 30px; border-radius: 16px 16px 0 0; text-align: center;">
            <h1 style="color: white; margin: 0; font-size: 24px; font-weight: 700;">ManageHub</h1>
          </div>
          <div style="background: white; padding: 40px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 16px 16px;">
            <h2 style="color: #1f2937; font-size: 20px; font-weight: 600; margin: 0 0 20px 0;">${subject}</h2>
            <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">${greeting}</p>
            <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 30px 0;">${body}</p>
            
            <div style="background: #f3f4f6; border-radius: 12px; padding: 20px; margin-bottom: 30px; border-left: 4px solid #3b82f6;">
              <p style="color: #1f2937; font-size: 14px; margin: 0; font-weight: 600;">${securityNote}</p>
              <p style="color: #4b5563; font-size: 14px; margin: 5px 0 0 0;">${securityBody}</p>
            </div>

            <p style="color: #4b5563; font-size: 16px; line-height: 1.6; margin: 0 0 10px 0;">${instruction}</p>
            <div style="text-align: center;">
              <a href="${baseUrl}" style="display: inline-block; background: #3b82f6; color: white; padding: 14px 32px; text-decoration: none; border-radius: 10px; font-weight: 600; font-size: 16px;">${signInText}</a>
            </div>
            
            <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
              <p style="color: #9ca3af; font-size: 12px; margin: 0;">${automatedText}</p>
            </div>
          </div>
        </div>
      `
    });

    return !result.error;
  } catch (e) {
    console.error('[NotificationSystem] Password reset email trigger failed:', e);
    return false;
  }
}

/**
 * Create notification for @mentions in forum or comments
 */
export async function createMentionNotification(mentionedUserId, mentionerName, contentType, contentId, contentTitle, commentPreview = null) {
  let link;
  if (contentType === 'fault_comment' || contentType === 'fault' || contentType === 'comment') {
    link = `/fault-reports/${contentId}`;
  } else if (contentType === 'topic' || contentType === 'reply') {
    link = `/forum/${contentId}`;
  } else {
    link = `/forum/${contentId}`;
  }

  const prefs = await getUserPreferences(mentionedUserId);
  if (prefs && prefs.notify_on_mentions === false) return;

  const lang = prefs?.language || 'en';
  const title = getTranslation('email.types.mention.title', lang, { name: mentionerName });
  let message = getTranslation('email.types.mention.message', lang, { title: contentTitle });

  if (commentPreview) {
    const preview = commentPreview.length > 100 ? commentPreview.substring(0, 100) + '...' : commentPreview;
    message = getTranslation('email.types.mention.preview', lang, { preview });
  }

  await notify(mentionedUserId, NOTIFICATION_TYPES.MENTION, title, message, link);
}

/**
 * Create notification when fault status changes
 */
export async function createFaultStatusNotifications(fault, oldStatus, newStatus) {
  const reporterId = fault.created_by || fault.reporter_id;
  if (!reporterId) return;

  const prefs = await getUserPreferences(reporterId);
  if (prefs && prefs.notify_fault_status === false) return;

  const lang = prefs?.language || 'en';
  const title = getTranslation('email.types.faultStatus.title', lang);
  const message = getTranslation('email.types.faultStatus.message', lang, {
    title: fault.title,
    oldStatus,
    newStatus
  });

  await notify(reporterId, NOTIFICATION_TYPES.FAULT_STATUS, title, message, `/fault-reports/${fault.id}`);
}

/**
 * Create notification when new comment is added to fault
 */
export async function createFaultCommentNotification(faultId, faultTitle, reporterId, commenterName) {
  if (!reporterId || !faultId) return;

  const prefs = await getUserPreferences(reporterId);
  if (prefs && prefs.notify_fault_status === false) return;

  const lang = prefs?.language || 'en';
  const title = getTranslation('email.types.faultComment.title', lang);
  const message = getTranslation('email.types.faultComment.message', lang, {
    name: commenterName,
    title: faultTitle
  });

  await notify(reporterId, NOTIFICATION_TYPES.FAULT_COMMENT, title, message, `/fault-reports/${faultId}`);
}

/**
 * Notify topic author when someone replies
 */
export async function createTopicReplyNotifications(reply, topic, replierName) {
  if (Number(topic.author_id) === Number(reply.author_id)) return;

  const prefs = await getUserPreferences(topic.author_id);
  if (!prefs || prefs.notify_topic_replies !== false) {
    const lang = prefs?.language || 'en';
    const title = getTranslation('email.types.topicReply.title', lang);
    const message = getTranslation('email.types.topicReply.message', lang, {
      name: replierName,
      title: topic.title
    });

    await notify(topic.author_id, NOTIFICATION_TYPES.TOPIC_REPLY, title, message, `/forum/${topic.id}`);
  }
}

/**
 * Notify all users about a new announcement
 */
export async function createAnnouncementNotifications(announcement, creatorName) {
  try {
    const { data: users, error } = await window.ezsite.apis.tablePage(TABLES.USERS_PROFILE, { PageNo: 1, PageSize: 1000 });
    if (error || !users?.List) return;

    for (const user of users.List) {
      const prefs = await getUserPreferences(user.id);
      if (!prefs || prefs.notify_announcements !== false) {
        const lang = prefs?.language || 'en';
        const title = getTranslation('email.types.announcement.title', lang);
        const message = getTranslation('email.types.announcement.message', lang, { title: announcement.title });
        await notify(user.id, NOTIFICATION_TYPES.ANNOUNCEMENT, title, message, '/announcements');
      }
    }
  } catch (e) {
    console.error('[NotificationSystem] Error creating announcement notifications:', e);
  }
}

/**
 * Notify relevant users about a new fault report
 */
export async function createFaultReportNotifications(fault, reporterName) {
  try {
    const { data: users, error } = await window.ezsite.apis.tablePage(TABLES.USERS_PROFILE, {
      PageNo: 1,
      PageSize: 1000
    });

    if (error || !users?.List) return;

    for (const user of users.List) {
      if (Number(user.id) === Number(fault.created_by)) continue;

      const prefs = await getUserPreferences(user.id);
      if (!prefs) continue;

      let shouldNotify = false;

      if (fault.property_id && Number(user.property_id) === Number(fault.property_id)) {
        if (prefs.notify_property_faults) shouldNotify = true;
      }

      if (fault.building_id && Number(user.building_id) === Number(fault.building_id)) {
        if (prefs.notify_building_faults) shouldNotify = true;
      }

      if (shouldNotify) {
        const lang = prefs.language || 'en';
        const title = getTranslation('email.types.faultNew.title', lang);
        const message = getTranslation('email.types.faultNew.message', lang, {
          name: reporterName,
          title: fault.title
        });

        await notify(user.id, NOTIFICATION_TYPES.FAULT_NEW, title, message, `/fault-reports/${fault.id}`);
      }
    }
  } catch (e) {
    console.error('[NotificationSystem] Error creating fault report notifications:', e);
  }
}